"use client";

// Delat läge för "är en mobil/läsplatta kopplad just nu?" på datorn.
// MobileQrButton läser /api/mobile-pair/current och QR-modalen vet exakt när en
// koppling tillkommer eller bryts — båda skriver hit, så att ScanLosning och
// GgLosningFoto kan byta knappuppsättning (platta ⇒ "Skicka till läsplattan")
// utan att varje uppgift pollar själv.
//
// Bevakare (2026-10-31, polling-analysen A7): bara de komponenter som visar
// fotoknappar (useParkoppling) behöver ett färskt läge. Medan minst en är
// monterad pollar MobileQrButton var 30:e s; annars läser den bara vid montering
// och när fliken syns. Den första bevakaren hämtar läget direkt, så det är aldrig
// gammalt där det används.

import { useEffect, useSyncExternalStore } from "react";

export type ParkopplingEnhet = "mobil" | "platta";

export interface Parkoppling {
  active: boolean;
  enhet: ParkopplingEnhet | null;
}

const INGEN: Parkoppling = { active: false, enhet: null };

let nuvarande: Parkoppling = INGEN;
// When the state was last read from the server (or set by the QR modal).
let lastAt = 0;
const lyssnare = new Set<() => void>();

export function sattParkoppling(p: Parkoppling): void {
  lastAt = Date.now();
  if (p.active === nuvarande.active && p.enhet === nuvarande.enhet) return;
  nuvarande = p.active ? p : INGEN;
  for (const l of lyssnare) l();
}

/** Tolkar svaret från GET /api/mobile-pair/current. */
export function parkopplingUrSvar(data: { active?: boolean; enhet?: unknown } | null): Parkoppling {
  if (!data?.active) return INGEN;
  const enhet = data.enhet === "platta" || data.enhet === "mobil" ? data.enhet : null;
  return { active: true, enhet };
}

/** Hämta läget direkt (t.ex. när QR-modalen just sett "active"). */
export async function uppdateraParkoppling(): Promise<void> {
  try {
    const res = await fetch("/api/mobile-pair/current", { cache: "no-store" });
    if (!res.ok) return;
    sattParkoppling(parkopplingUrSvar((await res.json()) as { active?: boolean; enhet?: unknown }));
  } catch {
    // tyst — nästa poll rättar till
  }
}

let bevakare = 0;

/** A component that shows photo buttons is mounted: the button should keep the state fresh. */
export function harBevakare(): boolean {
  return bevakare > 0;
}

function prenumerera(cb: () => void) {
  lyssnare.add(cb);
  return () => {
    lyssnare.delete(cb);
  };
}

/** The state for a component that acts on it (photo buttons): keeps it fresh while mounted. */
export function useParkoppling(): Parkoppling {
  useEffect(() => {
    bevakare += 1;
    // The first watcher reads at once unless the button (or the modal) just did.
    if (bevakare === 1 && Date.now() - lastAt > 5_000) void uppdateraParkoppling();
    return () => {
      bevakare -= 1;
    };
  }, []);
  return useParkopplingLage();
}

/** The state without asking for it to be kept fresh (MobileQrButton's icon colour). */
export function useParkopplingLage(): Parkoppling {
  return useSyncExternalStore(prenumerera, () => nuvarande, () => INGEN);
}
